import { DOMAttributes, useMemo } from "react";
import { useRouter } from "next/router";
import { Url } from "url";
import axios from "axios";

import useStore from "@store/store";
import {
  selectAccountData,
  selectOldStripeData,
} from "@store/slices/accountSlice";

import PricingTable from "@components/Pricing/PricingTable";
import InstallAddonCta from "@components/Utils/InstallAddonCta";
import ResubscribeCta from "@components/Utils/ResubscribeCta";
import { useUser } from "../../utils/AuthContext";
import { EXCEL_BOT_TOKEN } from "../../utils/constants";

const Profile = () => {
  const router = useRouter();
  const { user } = useUser();
  const accountData = useStore(selectAccountData);
  const oldStripeData = useStore(selectOldStripeData);

  const progressColor = useMemo(() => {
    if (accountData?.isPremium) return "progress-success";
    if (
      accountData?.creditBalance &&
      accountData?.creditBalance >= 0 &&
      accountData?.creditMax &&
      accountData?.creditMax > 0
    ) {
      const balancePercent = accountData
        ? 100 * (accountData?.creditBalance / accountData?.creditMax)
        : 0;
      if (balancePercent >= 50) return "progress-success";
      if (balancePercent >= 15 && balancePercent < 50)
        return "progress-warning";
      if (balancePercent < 15) return "progress-error";
    } else return "progress-error";
  }, [accountData]);

  const freeAccessDaysLeft = useMemo(() => {
    if (!oldStripeData) return null;

    return (
      (oldStripeData.current_period_end.seconds * 1000 - Date.now()) / 86400000
    );
  }, [oldStripeData]);

  const handleManageSubscription: DOMAttributes<HTMLFormElement>["onSubmit"] =
    async (e) => {
      e.preventDefault();
      const res = await axios.post(`/api/manage-subscription`, {
        customerId: accountData?.stripeCustomerId,
      }, {
        headers: {
          // @ts-ignore
          Authorization: `Bearer ${user.accessToken}`,
          ExcelBotToken: EXCEL_BOT_TOKEN,
        }
      });
      router.push(res.data as Url);
    };

  return (
    <div className="pt-5 pb-10 w-full h-full lg:scrollbar-thin lg:scrollbar-thumb-gray-500 lg:overflow-y-scroll lg:scrollbar-thumb-rounded-full lg:scrollbar-track-rounded-full">
      <h2 className="pl-8 pb-2 text-3xl font-bold border-b-2">Profile</h2>

      <div className="pl-8 mt-8 flex gap-10 flex-col md:items-center md:flex-row">
        <div className="avatar md:online">
          <div className="w-24 rounded-full">
            <img
              src={accountData?.photoURL as string}
              alt={accountData?.email as string}
            />
          </div>
        </div>

        <div className="form-control w-full">
          <label className="input-group">
            <span className="label-text">Email</span>
            <input
              type="text"
              className="input input-bordered w-full max-w-sm"
              defaultValue={accountData?.email as string}
              disabled={true}
            />
          </label>
        </div>
      </div>
      <div className="pl-8 mt-8">
        <InstallAddonCta />
      </div>

      {oldStripeData && <ResubscribeCta />}

      <div className="mt-12 mb-8 pl-8 pb-2 border-b-2 flex gap-4 items-center">
        <h3 className="text-3xl font-bold">Billing</h3>
        {accountData?.isPremium && (
          <div className="px-2.5 py-1 text-center text-sm font-medium rounded-full bg-green-400 duration-300 hover:bg-green-600 hover:text-white">
            Pro User
          </div>
        )}
      </div>
      <div className="lg:w-min lg:ml-8 p-4 border-2 bg-white rounded-xl max-w-full">
        <div className="mt-8 flex flex-col md:flex-row justify-start items-start gap-4">
          <div className="stats w-full">
            <div className="stat text-base-content w-full">
              <div className="stat-figure">
                <span className="material-icons">
                  {accountData?.creditBalance
                    ? "hourglass_top"
                    : "hourglass_empty"}
                </span>
              </div>
              <div className="stat-title">Balance Credits</div>
              <div className="stat-value">
                {accountData?.isPremium ? (
                  <p>Unlimited</p>
                ) : (
                  <p>{accountData?.creditBalance || 0}</p>
                )}
                <progress
                  className={`progress ${progressColor} lg:w-56 w-full`}
                  value={
                    accountData?.isPremium
                      ? 1
                      : accountData?.creditBalance
                        ? accountData?.creditBalance + 1
                        : 1
                  }
                  max={
                    accountData?.isPremium
                      ? 1
                      : accountData?.creditMax
                        ? accountData?.creditMax + 1
                        : 20
                  }
                ></progress>
              </div>
            </div>
          </div>
        </div>
        {accountData?.subscriptionId && !accountData?.subscriptionId.includes("I-") && !oldStripeData && (
          <form onSubmit={handleManageSubscription} className="mt-4">
            <button
              className={`px-5 py-2.5 text-center text-sm font-medium rounded-xl bg-green-400 duration-300 hover:bg-green-600 hover:text-white`}
              type="submit"
            >
              Manage Billing
            </button>
          </form>
        )}
      </div>

      {(!accountData?.subscriptionId || (freeAccessDaysLeft && freeAccessDaysLeft <= 30) || !accountData?.isPremium) && (
        <PricingTable showFree={false} />
      )}

      <div className="min-h-[5rem] md:hidden"></div>
    </div>
  );
};

export default Profile;
